import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import FilterListIcon from '@mui/icons-material/FilterList';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import React, { useCallback, useState } from 'react';

import { useStyles } from '../DataTable.styles';
import type { DataTableCellShell, DataTableCellValue, FilterOption } from '../DataTable.types';
import { SortDirection } from '../DataTable.types';
import { ColumnHeaderMenu } from './column-header-menu/ColumnHeaderMenu';
import { ResizeHandle } from './resize-handle/ResizeHandle';

export interface DataTableHeaderCellProps {
    shell: DataTableCellShell;
    isSortActive: boolean;
    sortDirection: SortDirection;
    onSortSet: (field: string, direction: SortDirection) => void;
    onSortClear: (field: string) => void;
    filterOptions?: FilterOption[];
    filterValues: DataTableCellValue[] | undefined;
    isFilterActive: boolean;
    onFilterChange: (field: string, values: DataTableCellValue[]) => void;
}

const DataTableHeaderCellInner = ({
    shell,
    isSortActive,
    sortDirection,
    onSortSet,
    onSortClear,
    filterOptions,
    filterValues,
    isFilterActive,
    onFilterChange,
}: DataTableHeaderCellProps) => {
    const { classes, cx } = useStyles();
    const { column } = shell;
    const sortable = column.sortable !== false;
    const hasDropdown = sortable || (filterOptions && filterOptions.length > 0);

    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const menuOpen = Boolean(menuAnchor);

    const handleCellClick = useCallback(
        (e: React.MouseEvent<HTMLTableCellElement>) => {
            // Guard: don't re-open if already open. React synthetic events bubble
            // through portal boundaries, so a backdrop click inside <Menu>/<Popover>
            // would otherwise bubble up here and immediately reopen the menu.
            if (menuAnchor) return;
            setMenuAnchor(e.currentTarget);
        },
        [menuAnchor],
    );

    const handleMenuClose = useCallback(() => {
        setMenuAnchor(null);
    }, []);

    const SortIcon = isSortActive
        ? sortDirection === SortDirection.ASC
            ? ArrowUpwardIcon
            : ArrowDownwardIcon
        : null;

    return (
        <TableCell
            className={cx(classes.cell, classes.headerCell, shell.isFirst && classes.cellFirst)}
            style={{ width: shell.width }}
            onClick={hasDropdown ? handleCellClick : undefined}
            automation-id={`column-header-${column.field}`}
        >
            <Box className={classes.headerCellContent}>
                {column.headerName}
                {SortIcon && <SortIcon className={classes.headerSortIcon} />}
                {isFilterActive && <FilterListIcon className={classes.headerFilterIcon} />}
                {hasDropdown && (
                    <ArrowDropDownIcon
                        className={cx(
                            classes.headerChevron,
                            (menuOpen || isSortActive || isFilterActive) &&
                                classes.headerChevronVisible,
                        )}
                    />
                )}
            </Box>
            <ResizeHandle shell={shell} automationId />
            {hasDropdown && (
                <ColumnHeaderMenu
                    anchorEl={menuAnchor}
                    field={column.field}
                    onClose={handleMenuClose}
                    sortable={sortable}
                    isSortActive={isSortActive}
                    sortDirection={sortDirection}
                    sortLabels={column.sortLabels}
                    onSortSet={onSortSet}
                    onSortClear={onSortClear}
                    filterOptions={filterOptions}
                    filterValues={filterValues}
                    onFilterChange={onFilterChange}
                />
            )}
        </TableCell>
    );
};

export const DataTableHeaderCell = React.memo(DataTableHeaderCellInner);
